import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../../../lib/api";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { useAuth } from "../../../../context/AuthContext";
import {
  CatalogLayoutShell, CatalogProductCard, CatalogTextTable, useAttrFacets,
  SELLER_TESTIDS,
} from "../../../../components/catalog-shared";
import { SectionHeading } from "../shared";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../../../components/ui/dialog";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Button } from "../../../../components/ui/button";

// Catalog-sync-specific constants — parity with SellerCatalog.jsx.
const CATALOG_SEARCH_FIELDS = [
  { value: "all", label: "All fields" },
  { value: "name", label: "Name" },
  { value: "sku", label: "SKU" },
  { value: "upc", label: "UPC" },
  { value: "product_type", label: "Type" },
  { value: "size", label: "Size" },
];
const CATALOG_SORT_OPTS = [
  { value: "name_asc",  label: "Name: A → Z" },
  { value: "name_desc", label: "Name: Z → A" },
  { value: "price_asc",  label: "Price: low → high" },
  { value: "price_desc", label: "Price: high → low" },
  { value: "stock_desc", label: "Stock: high → low" },
];

/**
 * CatalogSyncTab — the seller's dropship-only catalog mirror view.
 * Filter/sort/badge state parity with SellerCatalog.jsx, but scoped to
 * items the seller has enabled for dropship.
 * Extracted from SellerDropship.jsx in Phase 6 refactor.
 */
export default function CatalogSyncTab() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter/sort state — parity with SellerCatalog.jsx.
  const [search, setSearch] = useState("");
  const [searchField, setSearchField] = useState("all");
  const [badgeSet, setBadgeSet] = useState(new Set());
  const [cats, setCats] = useState(new Set());
  const [brandSet, setBrandSet] = useState(new Set());
  const [maxPrice, setMaxPrice] = useState(500);
  const [minMoq, setMinMoq] = useState(1);
  const [sort, setSort] = useState("name_asc");
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("verqo:dropship-catalog:view") || "grid"; } catch { return "grid"; }
  });
  useEffect(() => {
    try { localStorage.setItem("verqo:dropship-catalog:view", viewMode); } catch { /* ignore */ }
  }, [viewMode]);

  const load = () => {
    setLoading(true);
    api.get("/products")
      .then(r => {
        // Only dropship-eligible SKUs — wholesale-only ones stay in the
        // Catalog app and never surface in the dropship flow.
        const all = r.data || [];
        setItems(all.filter(p => p.fulfillment === "dropship" || p.fulfillment === "both"));
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => {
    const m = new Map();
    items.forEach(p => { if (p.category) m.set(p.category, (m.get(p.category) || 0) + 1); });
    return [...m.entries()].sort((a, b) => b[1] - a[1]).map(([key, count]) => ({ key, count }));
  }, [items]);
  const brands = useMemo(() => {
    const m = new Map();
    items.forEach(p => { if (p.brand) m.set(p.brand, (m.get(p.brand) || 0) + 1); });
    return [...m.entries()].sort((a, b) => b[1] - a[1]).map(([key, count]) => ({ key, count }));
  }, [items]);
  const { attrFilters, applyAttrFacets, attrSelKey } = useAttrFacets(items);

  const filtered = useMemo(() => {
    let r = items;
    if (badgeSet.size > 0) r = r.filter(p => badgeSet.has(p.badge));
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(p => {
        if (searchField === "name") return p.name?.toLowerCase().includes(q);
        if (searchField === "sku")  return p.sku?.toLowerCase().includes(q);
        if (searchField === "upc")  return p.upc?.toLowerCase().includes(q);
        if (searchField === "product_type") return p.product_type?.toLowerCase().includes(q);
        if (searchField === "size") return String(p.size || "").toLowerCase().includes(q);
        return [p.name, p.sku, p.upc, p.category, p.brand, p.sub_category, p.product_type, p.size, p.gender, p.condition, p.variant, p.ean, p.asin].some(v => v != null && String(v).toLowerCase().includes(q));
      });
    }
    if (cats.size > 0)     r = r.filter(p => cats.has(p.category));
    if (brandSet.size > 0) r = r.filter(p => brandSet.has(p.brand));
    r = applyAttrFacets(r);
    if (maxPrice < 500) r = r.filter(p => (p.price || 0) <= maxPrice);
    if (minMoq > 1)     r = r.filter(p => (p.moq || 1) >= minMoq);
    const arr = [...r];
    arr.sort((a, b) => {
      if (sort === "price_asc")  return (a.price || 0) - (b.price || 0);
      if (sort === "price_desc") return (b.price || 0) - (a.price || 0);
      if (sort === "name_desc")  return (b.name || "").localeCompare(a.name || "");
      if (sort === "stock_desc") return (b.stock || 0) - (a.stock || 0);
      return (a.name || "").localeCompare(b.name || "");
    });
    return arr;
  }, [items, search, searchField, badgeSet, cats, brandSet, applyAttrFacets, attrSelKey, maxPrice, minMoq, sort]);

  const brandName = user?.company || user?.tenant_name || null;
  const displayRows = useMemo(
    () => filtered.map(p => ({ ...p, seller_name: p.seller_name || brandName })),
    [filtered, brandName],
  );

  // Edit stays IN the dropship context — inline dialog covers the fields
  // that matter in a dropship workflow (name, price/retail/stock, handling
  // fee, description, image). For deep edits (variants/tiers/custom
  // fields), the dialog offers a "Open in full editor →" link that
  // navigates to /seller/catalog. This is the fix for the user-reported
  // bug where every product-card click bounced out to the Catalog app.
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // full product object
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const openEdit = (p) => {
    setEditing(p);
    setEditForm({
      name:                    p.name || "",
      sku:                     p.sku || "",
      brand:                   p.brand || "",
      category:                p.category || "",
      price:                   p.price ?? 0,
      retail_price:            p.retail_price != null ? String(p.retail_price) : "",
      dropship_handling_fee:   p.dropship_handling_fee != null ? String(p.dropship_handling_fee) : "",
      stock:                   p.stock ?? 0,
      moq:                     p.moq ?? 1,
      image:                   p.image || "",
      description:             p.description || "",
    });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        price:                 parseFloat(editForm.price) || 0,
        stock:                 parseInt(editForm.stock)  || 0,
        moq:                   parseInt(editForm.moq)    || 1,
        retail_price:          editForm.retail_price === ""          ? null : (parseFloat(editForm.retail_price)          || null),
        dropship_handling_fee: editForm.dropship_handling_fee === "" ? null : (parseFloat(editForm.dropship_handling_fee) || null),
      };
      await api.patch(`/products/${editing.id}`, payload);
      toast.success("Product updated");
      setEditOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };
  const onEdit = (p) => openEdit(p);
  const onDelete = async (id) => {
    if (!window.confirm("Delete this product? This removes it from your Catalog app entirely (both dropship and wholesale).")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  // Dropship-only field visibility: every SKU here is already dropship, so
  // the fulfillment badge is redundant. Catalog-app curation badges (New,
  // Featured, etc.) don't drive dropship ops decisions — hide them too.
  const dropshipVis = { fulfillment: false, badge: false };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <SectionHeading
          title="Your dropship catalog"
          sub="Live mirror of every SKU flagged as Dropship (or Both) in your Catalog app. Buyers with an active agreement see this exact list — filtered by any per-seller catalog rules from Setup."
        />
        <button
          onClick={() => nav("/seller/catalog")}
          data-testid="dropship-catalog-open-app"
          className="text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-9 px-3.5 border border-border hover:bg-secondary/50 inline-flex items-center gap-1.5 shrink-0"
        >
          <ExternalLink className="w-3.5 h-3.5"/>Manage in Catalog app
        </button>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-dashed border-border bg-secondary/20 p-12 text-center text-foreground/40 text-sm">Loading catalog…</div>
      ) : (
        <CatalogLayoutShell
          testids={SELLER_TESTIDS}
          search={search} setSearch={setSearch}
          searchField={searchField} setSearchField={setSearchField}
          searchFields={CATALOG_SEARCH_FIELDS} searchPlaceholder="Search your dropship SKUs, UPC, category…"
          badgeSet={badgeSet} setBadgeSet={setBadgeSet}
          sort={sort} setSort={setSort} sortOptions={CATALOG_SORT_OPTS}
          categories={categories} cats={cats} setCats={setCats} totalCount={items.length}
          productBrands={brands} brandSet={brandSet} setBrandSet={setBrandSet}
          attrFilters={attrFilters}
          // Fulfillment filter is meaningless here (tab is already filtered to
          // dropship+both) — pass inert defaults so the shell's "clear all"
          // doesn't crash, and hide the sidebar filter block outright.
          fulfillment="all" setFulfillment={() => {}}
          hideFulfillmentFilter
          maxPrice={maxPrice} setMaxPrice={setMaxPrice}
          minMoq={minMoq} setMinMoq={setMinMoq}
          viewMode={viewMode} setViewMode={setViewMode}
          filteredCount={filtered.length}
        >
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-foreground/45 border border-dashed border-border rounded-2xl">
              {items.length === 0 ? (
                <>
                  <div className="display text-xl">No dropship SKUs yet</div>
                  <p className="text-xs mt-1">Flag products as <strong>Dropship</strong> or <strong>Both</strong> in the Catalog app to make them visible here.</p>
                  <button onClick={() => nav("/seller/catalog")} data-testid="dropship-catalog-empty-cta"
                    className="mt-4 text-[11.5px] mono uppercase tracking-[0.14em] rounded-full h-9 px-4 bg-foreground text-background hover:bg-foreground/90 inline-flex items-center gap-1.5">
                    Open Catalog app <ExternalLink className="w-3 h-3"/>
                  </button>
                </>
              ) : (
                <>
                  <div className="display text-xl">No matches</div>
                  <p className="text-xs mt-1">Adjust your filters to see more products.</p>
                </>
              )}
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="seller-grid">
              {displayRows.map(p => (
                <CatalogProductCard key={p.id} mode="seller" view="grid" p={p} vis={dropshipVis} onEdit={() => onEdit(p)} onDelete={() => onDelete(p.id)}/>
              ))}
            </div>
          ) : viewMode === "list" ? (
            <div className="space-y-2" data-testid="seller-list">
              {displayRows.map(p => (
                <CatalogProductCard key={p.id} mode="seller" view="list" p={p} vis={dropshipVis} onEdit={() => onEdit(p)} onDelete={() => onDelete(p.id)}/>
              ))}
            </div>
          ) : (
            <CatalogTextTable mode="seller" rows={displayRows} vis={dropshipVis} onEdit={onEdit} onDelete={onDelete}/>
          )}
        </CatalogLayoutShell>
      )}

      {/* Inline edit dialog — keeps the seller in the Dropship context.
          Deep field edits (variants/tiers/custom fields) still route to
          the Catalog app via the footer link. */}
      <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dropship-catalog-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.sku ? `Edit · ${editing.sku}` : "Edit product"}</DialogTitle>
          </DialogHeader>
          <div className="text-[11.5px] text-foreground/60 -mt-2 mb-3">
            Update the dropship-relevant fields for this SKU. Changes save back to your Catalog app instantly.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">Name</label>
              <Input value={editForm.name || ""} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                data-testid="dropship-edit-name" className="h-9 text-[13px]"/>
            </div>
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">SKU</label>
              <Input value={editForm.sku || ""} onChange={e => setEditForm(f => ({ ...f, sku: e.target.value }))}
                data-testid="dropship-edit-sku" className="h-9 mono text-[12px]"/>
            </div>
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">Brand</label>
              <Input value={editForm.brand || ""} onChange={e => setEditForm(f => ({ ...f, brand: e.target.value }))}
                data-testid="dropship-edit-brand" className="h-9 text-[13px]"/>
            </div>
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">Wholesale price ($)</label>
              <Input type="number" step="0.01" value={editForm.price ?? 0}
                onChange={e => setEditForm(f => ({ ...f, price: e.target.value }))}
                data-testid="dropship-edit-price" className="h-9 mono"/>
            </div>
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">MSRP / retail ($)</label>
              <Input type="number" step="0.01" value={editForm.retail_price || ""}
                onChange={e => setEditForm(f => ({ ...f, retail_price: e.target.value }))}
                data-testid="dropship-edit-retail" className="h-9 mono"/>
            </div>
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">Stock</label>
              <Input type="number" value={editForm.stock ?? 0}
                onChange={e => setEditForm(f => ({ ...f, stock: e.target.value }))}
                data-testid="dropship-edit-stock" className="h-9 mono"/>
            </div>
            <div>
              <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">Dropship handling fee ($)</label>
              <Input type="number" step="0.01" value={editForm.dropship_handling_fee || ""}
                onChange={e => setEditForm(f => ({ ...f, dropship_handling_fee: e.target.value }))}
                data-testid="dropship-edit-handling" className="h-9 mono"/>
            </div>
            <div className="col-span-2">
              <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">Image URL</label>
              <Input value={editForm.image || ""} onChange={e => setEditForm(f => ({ ...f, image: e.target.value }))}
                data-testid="dropship-edit-image" className="h-9 text-[12px]"/>
            </div>
            <div className="col-span-2">
              <label className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 block mb-1">Description</label>
              <Textarea value={editForm.description || ""}
                onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                data-testid="dropship-edit-description" rows={3} className="text-[13px]"/>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-border">
            <button
              onClick={() => { setEditOpen(false); nav(`/seller/catalog?edit=${editing?.id}`); }}
              data-testid="dropship-edit-open-full"
              className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/55 hover:text-foreground inline-flex items-center gap-1"
            >
              Advanced fields (variants / tiers / custom) <ExternalLink className="w-3 h-3"/>
            </button>
            <div className="flex items-center gap-2">
              <button onClick={() => setEditOpen(false)}
                className="text-[11px] mono uppercase tracking-[0.14em] rounded-full h-9 px-4 border border-border hover:bg-secondary">
                Cancel
              </button>
              <Button onClick={saveEdit} disabled={saving}
                data-testid="dropship-edit-save"
                className="rounded-full h-9 px-4 text-[11px] mono uppercase tracking-[0.14em]">
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
